package org.catharsis.spendtel.feature.share

import org.catharsis.spendtel.feature.data.ExpenseReport
import org.catharsis.spendtel.feature.data.FinancialPersonality
import org.catharsis.spendtel.feature.data.GoalInsight
import org.catharsis.spendtel.feature.data.SpendingEquivalence
import org.catharsis.spendtel.feature.data.SubscriptionLeak
import org.catharsis.spendtel.feature.data.formatCurrency
import org.catharsis.spendtel.feature.legal.LegalConstants

data class ShareCardContent(
    val title: String,
    val text: String,
)

sealed class ShareImageCard {
    data class Highlight(val text: String) : ShareImageCard()

    data class Equivalence(
        val equivalence: SpendingEquivalence,
        val currency: String,
    ) : ShareImageCard()

    data class Personality(val personality: FinancialPersonality) : ShareImageCard()
}

private fun shareFooter(): String =
    "\n\nGenerated with ${LegalConstants.PRODUCT_NAME} · ${LegalConstants.WEBSITE}"

fun highlightShareContent(highlight: String): ShareCardContent =
    ShareCardContent(
        title = "${LegalConstants.PRODUCT_NAME} spotlight",
        text = highlight + shareFooter(),
    )

fun SpendingEquivalence.shareContent(currency: String): ShareCardContent =
    ShareCardContent(
        title = "$sourceLabel — ${LegalConstants.PRODUCT_NAME}",
        text = buildString {
            appendLine(comparison)
            appendLine(formatCurrency(amount, currency))
            if (insight.isNotBlank()) {
                appendLine(insight)
            }
            append(shareFooter())
        },
    )

fun FinancialPersonality.shareContent(): ShareCardContent =
    ShareCardContent(
        title = "$label — ${LegalConstants.PRODUCT_NAME}",
        text = buildString {
            appendLine(label)
            if (description.isNotBlank()) {
                appendLine(description)
            }
            append(shareFooter())
        },
    )

fun GoalInsight.shareContent(): ShareCardContent =
    ShareCardContent(
        title = "${LegalConstants.PRODUCT_NAME} goal insight",
        text = buildString {
            appendLine(insight)
            if (suggestion.isNotBlank()) {
                appendLine(suggestion)
            }
            append(shareFooter())
        },
    )

fun SubscriptionLeak.shareContent(currency: String): ShareCardContent =
    ShareCardContent(
        title = "Money leak — ${LegalConstants.PRODUCT_NAME}",
        text = buildString {
            appendLine("$name — ${formatCurrency(amount, currency)}/mo")
            append(shareFooter())
        },
    )

fun publicShareUrl(shareId: String): String = "${LegalConstants.WEBSITE}/share/$shareId"

fun ExpenseReport.toPublicShareSnapshot(): PublicShareSnapshot =
    PublicShareSnapshot(
        personalityLabel = financialPersonality?.label,
        personalityDescription = financialPersonality?.description,
        headline = monthlyStory?.headline,
        periodLabel = formatPeriodLabel(this),
        goalUsed = financialGoalUsed,
        highlights = highlights.take(5),
        equivalences = equivalences.take(3),
        highlightPreview = highlights.firstOrNull(),
        equivalencePreview = equivalences.firstOrNull(),
    )

private fun formatPeriodLabel(report: ExpenseReport): String? {
    return org.catharsis.spendtel.feature.data.formatStatementPeriodLabel(
        report.summary?.periodStart,
        report.summary?.periodEnd,
    ) ?: org.catharsis.spendtel.feature.data.formatReportDate(report.createdAtMillis)
}
